import { BASE_URL } from 'Utils/Constant.jsx';
import { AuthService } from '../services/AuthService';
import debugLogger from '../Utils/DebugLogger';

// Global flag to track backend availability
let backendAvailable = true;
let lastBackendCheckTime = 0;
const BACKEND_CHECK_INTERVAL = 30000; // 30 seconds

export function isBackendAvailable() {
  return backendAvailable;
}

export function setBackendAvailable(available) {
  backendAvailable = available;
  lastBackendCheckTime = Date.now();
}

async function request(path, { method = 'GET', headers = {}, body } = {}) {
  // Get token and add Authorization header if available
  const token = AuthService.getToken();
  
  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = body instanceof FormData;
  const mergedHeaders = {
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...headers,
  };
  
  // Log PUT requests to track payload
  if (method === 'PUT' && path.includes('api/applications/')) {
    debugLogger.log('API_PUT_REQUEST', 'PUT request to applications endpoint', {
      url: `${BASE_URL}${path}`,
      method,
      headers: mergedHeaders,
      bodyPayload: body,
      bodyStringified: body ? JSON.stringify(body) : undefined,
      path,
      timestamp: new Date().toISOString()
    });
  }
  
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: mergedHeaders,
      // Don't stringify FormData - send it as-is for file uploads
      body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
      credentials: 'include', // Include cookies in requests
      timeout: 10000, // 10 second timeout
    });
    
    // If we successfully reach the server, mark backend as available
    if (Date.now() - lastBackendCheckTime > BACKEND_CHECK_INTERVAL) {
      setBackendAvailable(true);
    }
    
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
      
      // Try to parse JSON error response
      if (text) {
        try {
          const errorData = JSON.parse(text);
          // If it's a structured error, preserve the structure
          if (errorData.error) {
            const error = new Error(errorData.error.message || errorMessage);
            error.error = errorData.error;
            error.status = res.status;
            throw error;
          } else if (errorData.message) {
            // Handle direct message format (like from our change password API)
            const error = new Error(errorData.message);
            error.status = res.status;
            error.code = errorData.code;
            throw error;
          } else {
            errorMessage = text;
          }
        } catch (parseError) {
          // If JSON parsing fails, use the text as is
          errorMessage = text;
        }
      }
      
      const error = new Error(errorMessage);
      error.status = res.status;
      throw error;
    }
    
    const responseText = await res.text();
    const responseData = responseText ? JSON.parse(responseText) : null;
    
    // Log PUT response
    if (method === 'PUT' && path.includes('api/applications/')) {
      debugLogger.log('API_PUT_RESPONSE', 'PUT response from applications endpoint', {
        url: `${BASE_URL}${path}`,
        status: res.status,
        statusText: res.statusText,
        responseData,
        responseText,
        timestamp: new Date().toISOString()
      });
    }
    
    return responseData;
      
  } catch (error) {
    // Check if it's a network error (server unreachable)
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      setBackendAvailable(false);
      throw new Error('NETWORK_ERROR: Backend server is not reachable');
    }
    
    // Check for timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      setBackendAvailable(false);
      throw new Error('TIMEOUT_ERROR: Backend server is not responding');
    }
    
    throw error;
  }
}

export const api = {
  get: (path, options = {}) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => request(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options = {}) => request(path, { ...options, method: 'PATCH', body }),
  del: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
};


